import { createSlice } from "@reduxjs/toolkit";
import { isEqual } from "lodash";
import { slices } from "@/constants/sliceNames";
import { DataSource } from "@/entities/DataSource";
import { Adjacent, Assignment, Prediction, ReviewStats } from "@/entities/DataSource/DataSource";
import { DataSourceMonitorData } from "@/entities/DataSource/DataSourceMonitorData";
import {
  requestClassificationDataSourceImageDetailsStatus,
  requestClassificationDataSourceMonitorDataStatus,
  requestClassificationMonitorPlateDataStatus,
  requestEditImageLabelStatus,
  requestMonitorLabelsListStatus,
} from "@/store/effects/data-source/classification-data-source-effects";
import {
  requestConnectDataSourceStatus,
  requestHoyaDataSourceImageDetailsStatus,
  requestDefaultDataSourceImageDetailsStatus,
  requestDataSourceMonitorDataStatus,
  requestDataSourcesListStatus,
  requestDeleteDataSourceStatus,
  requestDisconnectDataSourceStatus,
  requestSingleDataSourceDetailsStatus,
  requestCreateDataSourceStreamingStatus,
  requestEditDataSourceStreamingStatus,
  requestMonitorPlateDataStatus,
  requestRerunMonitorPlateDataStatus,
  requestMonitorPredictionsStatus,
  requestUpdateMonitorPredictionJudgmentStatus,
  requestMonitorImageAdjacentStatus,
  requestMonitorReviewStatsStatus,
  requestMonitorLabelsStatus,
  requestMonitorImageCountStatus,
  requestUpdateMonitorAssignImagesStatus,
  requestMonitorJudgmentStatsStatus,
  requestMonitorAssignmentsStatus,
  requestUpdateMonitorPredictionExcludeStatus,
  requestRerunImagePredictionStatus,
  requestRerunMultipleImagesPredictionStatus,
  requestMonitorUsedModelsStatus,
  requestMonitorExternalObjectJudgmentStatsStatus,
  requestMonitorStatsStatus,
  requestAllMonitorsStatsStatus,
} from "@/store/effects/data-source/data-source-effects";

const initialState = {
  /** @type {DataSource[]} */
  datasourceList: [],
  /** @type {DataSource} */
  datasource: DataSource.newInitial(),
  /** @type {boolean} */
  loading: false,
  /** @type {number} */
  currentPage: 0,
  /** @type {number} */
  pageLimit: 0,
  /** @type {number} */
  recordsCount: 0,
  /** @type {number} */
  totalPages: 0,
  /** @type {string} */
  message: "",
  /** @type {string} */
  error: "",

  connection: {
    /** @type {boolean} */
    loading: false,
    /** @type {string} */
    error: "",
    /** @type {string} */
    message: "",
  },

  /**@type {boolean} */
  dataLoading: false,

  /**@type {DataSourceMonitorData} */
  itemData: new DataSourceMonitorData(),

  /**@type {number} */
  totalItemData: 1,

  /**@type {number} */
  itemsCurrentPage: 1,

  /**@type {object} */
  imageDetails: {},

  /**@type {object} */
  plateData: {},

  /**@type {Array} */
  plateImages: [],

  /**@type {[]string} */
  labelsList: [],

  /** @type {boolean} */
  labelsListLoading: false,

  predictions: {
    /** @type {Prediction[]} */
    data: [],
    /** @type {number} */
    currentPage: 0,
    /** @type {number} */
    recordsCount: 0,
    /** @type {number} */
    pageLimit: 0,
    /** @type {boolean} */
    loading: false,
    dataLoading: false,
    /** @type {string[]} */
    labels: [],
    /** @type {object[]} */
    usedModels: [],
  },

  /** @type {Adjacent} */
  imageAdjacent: Adjacent.newInitial(),

  /** @type {ReviewStats} */
  reviewStats: ReviewStats.newInitial(),

  /** @type {boolean} */
  loadingJudgment: false,

  /** @type {object | null} */
  predictionJudgment: null,

  /** @type {boolean} */
  predictionJudgmentUpdated: false,

  /** @type {boolean} */
  monitorAssignImageUpdated: false,

  imageCounts: {
    /** @type {number} */
    data: -1,
    /** @type {boolean} */
    loading: false,
  },

  judgmentStats: {},

  externalObjectJudgmentStats: {},

  judgments: {
    /** @type {string[]} */
    data: [],
    /** @type {string[]} */
    prevData: [],
    /** @type {boolean} */
    dirty: false,
  },

  assignments: {
    /** @type {Assignment[]} */
    data: [],
    loading: false,
  },
  loadingExclude: false,

  monitorStats: {
    image_count: 0,
    progress_stats: {
      in_progress: 0,
      total: 0,
      done: 0,
    },
    folders: [],
    batch_process_dates: [],
    loading: false,
    error: null,
  },
  allMonitorsStats: {
    data: [],
    loading: false,
    error: null,
  },
};

const datasourceSlice = createSlice({
  name: slices.datasource,
  initialState,
  reducers: {
    resetDatasources: (state, action) => initialState,
    setJudgments: (state, action) => {
      state.judgments.data = action.payload;
      state.judgments.dirty = !isEqual(state.judgments.data, state.judgments.prevData);
    },
    resetImageCounts: (state) => {
      state.imageCounts = initialState.imageCounts;
    },
  },
  extraReducers: {
    ...requestDataSourcesListStatus,
    ...requestDisconnectDataSourceStatus,
    ...requestConnectDataSourceStatus,
    ...requestDeleteDataSourceStatus,
    ...requestSingleDataSourceDetailsStatus,
    ...requestDataSourceMonitorDataStatus,
    ...requestHoyaDataSourceImageDetailsStatus,
    ...requestDefaultDataSourceImageDetailsStatus,
    ...requestCreateDataSourceStreamingStatus,
    ...requestEditDataSourceStreamingStatus,
    ...requestMonitorPlateDataStatus,
    ...requestClassificationDataSourceMonitorDataStatus,
    ...requestClassificationMonitorPlateDataStatus,
    ...requestClassificationDataSourceImageDetailsStatus,
    ...requestRerunMonitorPlateDataStatus,
    ...requestMonitorLabelsListStatus,
    ...requestEditImageLabelStatus,
    ...requestMonitorPredictionsStatus,
    ...requestMonitorImageCountStatus,
    ...requestUpdateMonitorAssignImagesStatus,
    ...requestMonitorAssignmentsStatus,
    ...requestUpdateMonitorPredictionJudgmentStatus,
    ...requestMonitorImageAdjacentStatus,
    ...requestMonitorReviewStatsStatus,
    ...requestMonitorLabelsStatus,
    ...requestMonitorUsedModelsStatus,
    ...requestMonitorJudgmentStatsStatus,
    ...requestUpdateMonitorPredictionExcludeStatus,
    ...requestRerunImagePredictionStatus,
    ...requestRerunMultipleImagesPredictionStatus,
    ...requestMonitorExternalObjectJudgmentStatsStatus,
    ...requestMonitorStatsStatus,
    ...requestAllMonitorsStatsStatus,
  },
});

const { actions, reducer } = datasourceSlice;

export const { resetDatasources, setJudgments, resetImageCounts } = actions;

export default reducer;
